import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Eye, EyeOff, Mail, Lock, AlertCircle, ArrowLeft } from 'lucide-react';

const LoginForm = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  // Redirecionar se já estiver logado
  React.useEffect(() => {
    if (user) {
      navigate('/agenda', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Por favor, preencha todos os campos');
      return;
    }

    try {
      setError('');
      setLoading(true);
      
      await login(email, password);
      navigate('/agenda', { replace: true });
    } catch (error) {
      // Erro no login
      // Tratar erros do Firebase
      const errorCode = error.code;
      
      if (errorCode === 'auth/invalid-credential' || errorCode === 'auth/wrong-password') {
        setError('Email ou senha incorretos');
      } else if (errorCode === 'auth/user-not-found') {
        setError('Email não encontrado');
      } else if (errorCode === 'auth/invalid-email') {
        setError('Email inválido');
      } else if (errorCode === 'auth/user-disabled') {
        setError('Usuário desabilitado');
      } else if (errorCode === 'auth/too-many-requests') {
        setError('Muitas tentativas. Tente novamente mais tarde');
      } else if (errorCode === 'auth/network-request-failed') {
        setError('Erro de conexão. Verifique sua internet');
      } else if (error.message === 'Senha incorreta') {
        setError('Senha incorreta');
      } else if (error.message === 'Email não encontrado') {
        setError('Email não encontrado');
      } else if (error.message === 'Email ou senha incorretos') {
        setError('Email ou senha incorretos');
      } else {
        setError(error.message || `Erro ao fazer login: ${errorCode || 'Erro desconhecido'}. Tente novamente.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background-light dark:bg-background-dark py-8 sm:py-12 px-4 sm:px-6 lg:px-8">
      {/* Botão Voltar para Home */}
      <button
        onClick={() => navigate('/')}
        className="absolute top-4 left-4 sm:top-6 sm:left-6 flex items-center space-x-1 sm:space-x-2 bg-card-light dark:bg-card-dark text-foreground-light dark:text-foreground-dark hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 px-3 sm:px-4 py-2 rounded-lg border border-border-light dark:border-border-dark transition-all duration-200 shadow-sm hover:shadow-md text-sm sm:text-base"
      >
        <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
        <span className="font-medium hidden sm:inline">Voltar ao Site</span>
        <span className="font-medium sm:hidden">Voltar</span>
      </button>

      <div className="max-w-md w-full space-y-6 sm:space-y-8">
        <div>
          <div className="flex flex-col items-center mb-8 sm:mb-12 -mt-3">
            {/* Logo */}
            <img 
              src="/Preto.png" 
              alt="Aruk Dog" 
              className="w-28 h-28 sm:w-38 sm:h-38 md:w-46 md:h-46 -mb-4"
            />
            
            {/* Logo com mesmo estilo da navbar */}
            <span className="inline-flex flex-col items-stretch">
              <span className="font-bold flex items-baseline text-xl sm:text-2xl md:text-3xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                <span className="text-primary-light dark:text-primary-dark">Aruk Dog</span>
              </span>
              <span className="block h-0.5 bg-border-light dark:bg-border-dark mt-1"></span>
            </span>
          </div>
        </div>
        
        <form className="mt-6 sm:mt-8 space-y-4 sm:space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-destructive-50 dark:bg-destructive-dark/20 border border-destructive-200 dark:border-destructive-dark text-destructive-700 dark:text-destructive-light px-3 sm:px-4 py-2 sm:py-3 rounded-lg flex items-center space-x-2 text-sm sm:text-base">
              <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
          
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="label">
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-5 w-5 text-gray-400 dark:text-muted-dark" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-field pl-10"
                  placeholder="seu@email.com"
                />
              </div>
            </div>
            
            <div>
              <label htmlFor="password" className="label">
                Senha
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-5 w-5 text-gray-400 dark:text-muted-dark" />
                </div>
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="input-field pl-10 pr-10"
                  placeholder="Sua senha"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400 dark:text-muted-dark hover:text-gray-600 dark:hover:text-foreground-dark" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400 dark:text-muted-dark hover:text-gray-600 dark:hover:text-foreground-dark" />
                  )}
                </button>
              </div>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-background-light dark:focus:ring-offset-background-dark focus:ring-ring-light dark:focus:ring-ring-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? 'Entrando...' : 'Entrar'}
            </button>
          </div>

        </form>
      </div>
    </div>
  );
};

export default LoginForm;
